import React, { useEffect, useRef, useState } from 'react';
import { DisplayMessage } from '@/types';
import { Language } from '../../../shared/types';
import { cn, friendlyTimestamp, getSourceAudioURL } from '@/lib/utils';
import { Loader2, MessageCircleDashed, ThumbsUp, ThumbsDown, MessageCircleMore, PauseCircle, PlayCircle, Trash, TriangleAlert, Download } from 'lucide-react';
// import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"; // ShadCN Avatar
import { Button } from '../ui/button';
import { useTranslation } from '@/lib/translate';
import { FilterType, TextSize } from './ChatArea';
import MagicIcon from '../icons/Magic';
import JAIcon from '../icons/languages/ja';
import ZHIcon from '../icons/languages/zh';
import EnIcon from '../icons/languages/en';
import SpeakerIcon from '../icons/Speaker';
import { useAppStore } from '@/hooks/useAppStore';
import { DDiv } from '../ui/ddiv';
import { supportedLanguages } from '../layout/LanguageSelector';
import { apiFetch } from '@/lib/fetch';

// Simple SVG Icons (Replace with lucide-react or other library if preferred)
const LanguageIcon: React.FC<{ lang: Language }> = ({ lang }) => {
  if (lang === 'ja') { // Japanese
    return <JAIcon className='rounded-lg border-black' />;
  } if (lang === 'zh') { // Chinese
    return <ZHIcon />
  } else if (lang === 'en') { // English
    return <EnIcon />
  } else {
    return <MagicIcon fill="darkgreen" width="22" height="22" />
  }
};

interface MessageBubbleProps {
  message: DisplayMessage;
  preferredLanguage: Language; // To format timestamp
  filter: FilterType;
  hideOriginalText: boolean;
  textSize?: TextSize;
  setCommentTranslationDialogOpen: (open: boolean) => void;
  setCommentContent: React.Dispatch<React.SetStateAction<{
    id: string;
    comment: string;
    commentType: string;
    email: string;
    expectedTranscription?: string;
    expectedTranslation?: string;
    currentTranscription?: string;
    translations?: Record<string, string>;
    targetLanguage?: string;
  }>>;
  onDoubleClick?: (message: DisplayMessage) => void;
  presentationMode: boolean;
  roomId: string | null;
};

const getMarginForTextSize = (textSize: TextSize) => {
  switch (textSize) {
    case 'base':
      return 'mb-0'; // Smaller margin for smaller text
    case 'lg':
    case 'xl':
    case '2xl':
      return 'mb-[2em]';
    case '4xl':
      return 'mb-[3.5em]';
    case '6xl':
      return 'mb-[5em]'; // Larger margin for larger text
    default:
      return 'mb-1';
  }
};

const ttsCache = new Map<string, string>(); // text -> audio URL

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  preferredLanguage,
  filter,
  textSize,
  hideOriginalText,
  setCommentTranslationDialogOpen,
  setCommentContent,
  onDoubleClick,
  presentationMode = false,
  roomId,
}) => {
  const {
    id,
    displayUsername,
    username,
    timestamp,
    message: transcription,
    language: sourceLanguage,
    translations,
    isMe,
    userColor,
    // userColorDarker,
    notes,
    isLiked,
    comment,
    commentType,
    email,
    expectedTranscription,
    expectedTranslation
  } = message;

  const t = useTranslation(preferredLanguage);
  const [showNote, setShowNote] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const deleteMessage = useAppStore((state) => state.deleteMessage);
  const reactMessage = useAppStore((state) => state.reactMessage);
  const adminUser = useAppStore(state => state.adminUser);
  const isAdmin = !!adminUser;
  /*
  on same device
  if target language is spoken, translate to interface language
  if any other language is spoken, translate to target language
  ----
  additional device
  it any language is spoken on other devices, translate in the current device to interface language
  */
  // const displayLanguage = (!isMe || message.language == targetLanguage ? preferredLanguage : targetLanguage);

  // Determine which languages to display 
  const targetLanguages = useAppStore((state) => state.targetLanguages);

  const [translationLoadingMap, setTranslationLoadingMap] = useState<Record<string, boolean>>({});
  const translateMessage = useAppStore((state) => state.translateMessage);
  const clientConfig = useAppStore((state) => state.clientConfig);
  const diffSec = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);

  // Audio state
  const [playingLang, setPlayingLang] = useState<string | null>(null);

  const setAudioUrl = async (lang: string, text: string, url: any) => {
    if (!audioRef.current) {
      return
    }
    audioRef.current.src = url;
    audioRef.current.play().catch(err => {
      console.error("Playback failed:", err);
      setPlayingLang(null);
    }).then(() => {
      ttsCache.set(text, url);
      setPlayingLang(lang);
    });
  }

  const handleSpeakClick = async (lang: string, text: string) => {
    if (playing && playingLang === lang) {
      return pauseAudio();
    }
    // If playing another language, stop it first (implicitly done by src change but better to be clean)
    if (paused && playingLang === lang) {
      return resumeAudio();
    }
    setError(null);
    if (ttsCache.has(text)) {
      setAudioUrl(lang, text, ttsCache.get(text)!);
      return;
    }
    setPlayingLang(lang); // Optimistic setting to show spinner if we used that for loading state
    setLoading(true);
    try {
      const res = await apiFetch(`/tts?text=${encodeURIComponent(text)}&lang=${lang}`); // Assuming backend accepts lang hint or auto-detect
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errorData = await res.json();
        setError("TTS request failed: " + (errorData.detail || "unknown error"));
        setPlayingLang(null);
        return;
      }
      if (!res.ok) {
        setError(`Request failed with status ${res.status}`);
        setPlayingLang(null);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(lang, text, url);
    } catch (err) {
      console.error("TTS request failed:", err);
      setError("Failed to connect to server");
      setPlayingLang(null);
    } finally {
      setLoading(false);
    }
  };

  const pauseAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setPaused(true);
    setPlaying(false);
    setLoading(false);
  }

  const resumeAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.play().catch(e => {
      console.error("Error resuming media", e)
    });
    setPaused(false);
    setPlaying(true);
    setLoading(false);
  }

  const handleLoadTranslationClick = async (id: string, lang: Language) => {
    setTranslationLoadingMap(prev => ({ ...prev, [lang]: true }));
    translateMessage(id, lang);
  }

  // Effect to clear loading state when translation arrives
  useEffect(() => {
    Object.keys(translationLoadingMap).forEach(lang => {
      if (translations[lang as Language] && translationLoadingMap[lang]) {
        setTranslationLoadingMap(prev => ({ ...prev, [lang]: false }));
      }
    });
  }, [translations, translationLoadingMap]);

  const hasTranslations = Object.keys(translations).length > 0;

  const downloadAudio = (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomId) {
      return;
    }
    const audioUrl = getSourceAudioURL(id, roomId, username);
    // Open audio file in a new tab/window
    window.open(audioUrl, "_blank");
  };

  const likeTranslation = (id: string, value?: boolean) => {
    reactMessage(id, value);
  }

  const dislikeTranslation = (id: string, value?: boolean) => {
    reactMessage(id, value);
  }

  const commentTranslation = (id: string) => {
    setCommentTranslationDialogOpen(true);
    setCommentContent({ id, comment: comment || "", commentType: commentType || "", email: email || "", expectedTranscription: expectedTranscription || "", expectedTranslation: expectedTranslation || "", currentTranscription: transcription, translations, targetLanguage: "", messageAuthor: username });
  }

  // Sometimes the notes come back in quotes
  let cleanedNote: string | null = null;
  
  if (notes && notes[preferredLanguage]) {
    const regex = /^["'„‟‚‛‟＂＇｀｛「『【《〈《〔（*]+|\s*["'„‟‚‛‟＂＇｀｝」』】》〉》〕）*]+$/g;
    cleanedNote = notes[preferredLanguage].replace(regex, '');
  } else if (notes) {
      const firstNote = Object.values(notes)[0];
      if (firstNote) {
        const regex = /^["'„‟‚‛‟＂＇｀｛「『【《〈《〔（*]+|\s*["'„‟‚‛‟＂＇｀｝」』】》〉》〕）*]+$/g;
        cleanedNote = firstNote.replace(regex, '');
      }
  }

  // --- Consolidated Display Queue Logic ---
  // 1. Start with targetLanguages (user preference order).
  // 2. If sourceLanguage is missing from the list and not hidden, append it to the end (default behavior).
  // 3. Filter out sourceLanguage if hideOriginalText is true.
  // 4. If bouncerRejected, show only the first target language with the error message.
  let displayQueue = [...targetLanguages];
  if (displayQueue.length === 0) {
     displayQueue.push(preferredLanguage);
  }

  // Ensure unique
  displayQueue = Array.from(new Set(displayQueue));

  // If source language is NOT in the list, and we want to show it:
  // We only append it if it is NOT a supported language (i.e., user couldn't select it).
  // If it IS a supported language (e.g. English) but user didn't select it, we hide it.
  const isSourceSupported = supportedLanguages.some(l => l.code === sourceLanguage);
  
  if (!displayQueue.includes(sourceLanguage) && !isSourceSupported && !hideOriginalText) {
    displayQueue.push(sourceLanguage);
  }

  // If hideOriginalText is true, remove sourceLanguage from the list
  if (hideOriginalText) {
    displayQueue = displayQueue.filter(lang => lang !== sourceLanguage);
  }

  // BOUNCER: If message was rejected, show only single error line
  const bouncerRejected = message.bouncerRejected;
  if (bouncerRejected) {
    displayQueue = [displayQueue[0] || preferredLanguage];
  }

  return (
    <DDiv
      className={cn(`message-container flex flex-col animate-fadeIn`,
        presentationMode ? "items-start tracking-wider text-shadow-lg/70" : "items-end mb-3",
        presentationMode && getMarginForTextSize(textSize as TextSize)
      )}
      data-timestamp={id}
      onDoubleClick={() => onDoubleClick?.(message)}
    >
      <audio
        ref={audioRef}
        hidden
        onWaiting={() => setLoading(true)}
        onCanPlay={() => setLoading(false)}
        onPlaying={() => {
          setPlaying(true)
          setLoading(false)
        }}
        onEnded={() => {
          setPlaying(false);
          setLoading(false);
          setPlayingLang(null);
        }}
      />

      {/* Message Content */}
      <div
        className={cn('flex w-fit flex-col',
          presentationMode ? "" : "w-[100%] rounded-lg shadow-md py-3 px-3",
        )}
        style={{
          backgroundColor: presentationMode ? 'transparent' : userColor,
          textShadow: presentationMode ? 'none' : "0.02rem 0.07rem 0.2rem rgba(255, 255, 255, 1)",
          fontWeight: 400,
          boxShadow: presentationMode ? 'none' : "rgba(0, 0, 0, 0.12) 0 0.3rem 1.5rem, rgba(255, 255, 255, 0.5) 0 0 0 0.075rem inset",
          border: presentationMode ? 'none' : "0.075rem solid rgba(255, 255, 255, 0.4)",
          borderRadius: presentationMode ? 0 : '1.2rem'
        }}
      >
        {/* Unified Display Loop */}
        {displayQueue.map((lang, index) => {
          const isFirst = index === 0;
          const isLast = index === displayQueue.length - 1;
          // For bouncer-rejected messages, treat first language as source to show the error message
          const isSource = bouncerRejected ? isFirst : (lang === sourceLanguage);

          // Define secondary colors for presentation mode styling
          const secondaryColors = [
            "text-[var(--color-teal-400)]",
            "text-green-400",
            "text-orange-400",
            "text-yellow-400"
          ];
          
          const presentationClass = presentationMode 
            ? (index === 0 
                ? "text-white" 
                : secondaryColors[(index - 1) % secondaryColors.length])
            : "";

          const presentationSize = presentationMode
            ? (index === 0 ? textSize : "2xl")
            : textSize;

          // --- TRANSCRIPTION BLOCK ---
          if (isSource) {
            // For bouncer-rejected messages, use the target language for display
            const displayLang = bouncerRejected ? lang : sourceLanguage;
            return (
              <div
                key={`${id}-transcription`}
                className={cn("message transcription flex items-start gap-2",
                  "text-" + presentationSize,
                  presentationMode ? "px-2" : "p-1.5",
                  isFirst && "rounded-t-lg",
                  isLast && "rounded-b-lg",
                  !isFirst && !isLast && "rounded-none"
                )}
                lang={displayLang}
              >
                <div className={cn("mt-0.5",
                  presentationMode ? "hidden" : ""
                )}>
                  <LanguageIcon lang={displayLang} />
                </div>
                <div className={cn("trans-text flex-1 select-text break-words whitespace-pre-wrap [line-break:loose]",
                    presentationMode ? presentationClass : filter === "showOnlyMine" ? "text-teal-400" : "text-[#666]",
                    presentationMode ? "leading-relaxed tracking-wider font-sans" : "font-serif"
                  )}
                  style={presentationMode ? {} : (clientConfig?.theme?.light?.languageStyles?.[sourceLanguage] || {})}
                >
                  {transcription}
                </div>
                <div className="flex gap-1">
                  {(isAdmin) && <button
                    onClick={downloadAudio}
                    title={t("Download audio source")}
                    className={cn(
                      'rounded-full p-1 text-gray-800 hover:shadow-xs',
                      'cursor-pointer',
                      filter == "showOnlyMine" ? "text-gray-100" : "hover:bg-[#2a4d52] hover:text-gray-50"
                    )}
                  >
                    <Download className='w-4 h-4' />
                  </button>
                  }
                  {(isAdmin || (isMe && !presentationMode)) && <button
                    onClick={() => {
                      if (window.confirm(t('Are you sure you want to delete this message?'))) {
                        deleteMessage(id);
                      }
                    }}
                    title={t("Delete this translation")}
                    className={cn(
                      'group rounded-full p-1 text-gray-800 hover:shadow-xs',
                      'cursor-pointer',
                      filter == "showOnlyMine" ? "text-gray-100" : "hover:bg-[#2a4d52] hover:text-gray-50"
                    )}
                  >
                    <Trash className='w-4 h-4 text-black opacity-20 group-hover:text-white group-hover:opacity-100' />
                  </button>
                  }
                </div>
              </div>
            );
          }

          // --- TRANSLATION BLOCK ---
          const translatedText = translations[lang];
          const isThisPlaying = playing && playingLang === lang;
          const isThisLoading = loading && playingLang === lang;
          const isThisPaused = paused && playingLang === lang;
          const isTranslationLoading = translationLoadingMap[lang];

          if (translatedText) {
             return (
               <div
                key={`${id}-trans-${lang}`}
                className={cn("message translation flex items-start gap-2",
                  presentationMode ? "px-2" : "p-1.5",
                  isFirst && "rounded-t-lg",
                  isLast && "rounded-b-lg",
                  !isFirst && !isLast && "rounded-none",
                  "text-" + presentationSize
                )}
                lang={lang}
              >
                <div className={cn("mt-0.5",
                  presentationMode ? "hidden" : ""
                )}>
                  <LanguageIcon lang={lang as Language} />
                </div>
                <div className={cn("trans-text flex-1 select-text",
                    presentationMode ? presentationClass : filter === "showOnlyMine" ? "text-white" : "text-[#666]",
                    presentationMode ? "leading-relaxed tracking-wide font-sans" : "font-serif"
                  )}
                  style={presentationMode ? {} : (clientConfig?.theme?.light?.languageStyles?.[lang as string] || {})}
                >
                  {translatedText}
                </div>
                <button
                  onClick={() => handleSpeakClick(lang, translatedText)}
                  disabled={isThisLoading}
                  title={error ? error : ""}
                  className={cn('group rounded-full p-1 text-gray-800 hover:shadow-xs',
                    isThisLoading ? "" : "cursor-pointer",
                    error && playingLang === lang ? "bg-red-500" : filter == "showOnlyMine" ? "text-gray-100" : "hover:bg-[#2a4d52] hover:text-gray-50",
                    presentationMode ? "hidden" : ""
                  )}
                >
                  {isThisLoading ?
                    <Loader2 className={cn('w-4 h-4 animate-spin')} />
                    : isThisPlaying ?
                      <PauseCircle className={cn('w-4 h-4')} />
                      : isThisPaused ?
                        <PlayCircle className={cn('w-4 h-4')} /> : (error && playingLang === lang) ?
                          <TriangleAlert className='w-3 h-3 text-white' />
                          : <SpeakerIcon className={cn('w-4 h-4 text-black opacity-20 group-hover:text-white group-hover:opacity-100')} />}

                </button>
              </div>
             );
          } else {
             // Missing translation
             if (hasTranslations || diffSec > 10) {
               return (
                <div
                  key={`${id}-missing-${lang}`}
                  className={cn("message translation flex items-start gap-2",
                    presentationMode ? "px-2" : "p-1.5",
                    isFirst && "rounded-t-lg",
                    isLast && "rounded-b-lg",
                    !isFirst && !isLast && "rounded-none",
                    "text-" + presentationSize
                  )}
                >
                  <div className={cn("mt-0.5",
                    presentationMode ? "hidden" : ""
                  )}>
                    <LanguageIcon lang={lang as Language} />
                  </div>
                  <div className={cn("trans-text flex-1 select-text",
                      presentationMode ? presentationClass : filter === "showOnlyMine" ? "text-white" : "text-[#666]",
                      presentationMode ? "tracking-wide font-light font-sans" : "font-serif")
                    }
                    style={presentationMode ? {} : (clientConfig?.theme?.light?.languageStyles?.[lang as string] || {})}
                  >
                    {isTranslationLoading ? t("[Loading translation...]") : t("[Translation not loaded]")}
                  </div>
                  <button
                    onClick={() => handleLoadTranslationClick(id, lang as Language)}
                    disabled={isTranslationLoading}
                    title={error ? error : ""}
                    className={cn('py-1 px-3 items-center text-xs rounded-full',
                      isTranslationLoading ? "opacity-60" : "cursor-pointer",
                      presentationMode ? presentationClass : "text-gray-800",
                      presentationMode ? "hover:opacity-80 underline" : (filter == "showOnlyMine" ? "text-gray-100 hover:bg-gray-100 hover:text-gray-900" : "hover:bg-[#2a4d52] hover:text-gray-50"),
                    )}
                  >
                    {t("Click to load")}
                  </button>
                </div>
               );
             }
             return null;
          }
        })}

        {/* Nuance if available */}
        {!isMe && !!cleanedNote && (
          <div
            className={cn(
              "font-serif text-[#666] text-sm transition-all duration-300 ease-in-out select-text overflow-hidden",
              showNote ? "max-h-[500px] opacity-100 p-1.5" : "max-h-0 opacity-0"
            )}
          >
            <MessageCircleDashed
              className="inline"
              style={{ height: "1em" }}
            ></MessageCircleDashed>
            {cleanedNote}
          </div>
        )}
      </div>

      {/* Combined User Info and Interaction Buttons Row */}
      {!presentationMode && !isAdmin && filter !== "showOnlyMine" && <div className="flex items-center justify-between py-1 w-full">
        {/* User Info - Left side */}
        <div 
          className="flex items-center gap-1 text-xs"
          style={{color: "rgba(0, 0, 0, 0.5)"}}
        >
          {!isMe && !!cleanedNote && (
            <Button
              size="xs"
              className="rounded-full px-3 py-0.5 gap-0 text-xs uppercase bg-purple-700 text-white"
              onClick={() => setShowNote(!showNote)}
            >
              {t(`HINT`)}
              <MessageCircleDashed
                style={{ height: "1em" }}
              ></MessageCircleDashed>
            </Button>
          )}
          <span className="font-semibold">
            {'@ ' + displayUsername}
            {/* {isMe ? t(" (You)") : ""} */}
          </span>
          {/* <span className="text-neutral-500">•</span> */}
          <span>
            {friendlyTimestamp(timestamp, preferredLanguage)}
          </span>
        </div>

        {/* Interaction Buttons - Right side */}
        <div className="flex items-center">
          {/* Thumbs Up */}
          {isLiked !== false && (
            <button
              title="Like"
              onClick={() => likeTranslation(id, isLiked === true ? undefined : true)}
              className={`p-1 m-1 rounded-full transition transform hover:scale-120 active:scale-95 ${
                isLiked === true ? "bg-green-600 text-white" : ""
              }`}
            >
              <ThumbsUp className="w-4 h-4" />
            </button>
          )}
          {/* Thumbs Down */}
          {isLiked !== true && (
            <button
              title="Dislike"
              onClick={() => dislikeTranslation(id, isLiked === false ? undefined : false)}
              className={`p-1 m-1 rounded-full transition transform hover:scale-120 active:scale-95 ${
                isLiked === false ? "bg-red-600 text-white" : ""
              }`}
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
          )}

          {/* Comment */}
          <button
            title='Comment'
            onClick={() => commentTranslation(id)}
            className="p-1 m-1 rounded-full transition transform hover:scale-120 active:scale-95"
          >
            <MessageCircleMore className="w-4 h-4" />
          </button>
        </div>
      </div>}
    </DDiv>
  );
};
